import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Landmark, Loader2, Save, Plus, X, ChevronRight } from 'lucide-react';

import {
    fetchTDSNames,
    fetchTDSDCANames,
    fetchTDSSDCANames,
    submitNewTDSName,
    fetchTDSConfigGrid,
    submitTDSConfigDetails,
    clearSDCAList,
    clearTDSSaveResult,
    clearAddNameResult,
    selectTDSNameList,
    selectTDSDCAList,
    selectTDSSDCAList,
    selectTDSGridList,
    selectTDSSaveResult,
    selectTDSAddNameResult,
    selectTDSNamesLoading,
    selectTDSDCALoading,
    selectTDSSDCALoading,
    selectTDSGridLoading,
    selectTDSSaveLoading,
    selectTDSAddNameLoading,
    selectTDSGridError,
    selectTDSSaveError,
    selectTDSAddNameError,
} from '../../slices/accountsSlice/tdsConfigureSlice';
import { showToast } from '../../utilities/toastUtilities';

const todayISO = () => new Date().toISOString().slice(0, 10);

const TDSConfigure = () => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userDetails?.userName || 'system';

    const tdsNameList = useSelector(selectTDSNameList);
    const dcaList     = useSelector(selectTDSDCAList);
    const sdcaList    = useSelector(selectTDSSDCAList);
    const gridList    = useSelector(selectTDSGridList);
    const saveResult      = useSelector(selectTDSSaveResult);
    const addNameResult   = useSelector(selectTDSAddNameResult);

    const tdsNamesLoading = useSelector(selectTDSNamesLoading);
    const dcaLoading      = useSelector(selectTDSDCALoading);
    const sdcaLoading     = useSelector(selectTDSSDCALoading);
    const gridLoading     = useSelector(selectTDSGridLoading);
    const gridError       = useSelector(selectTDSGridError);
    const saveLoading     = useSelector(selectTDSSaveLoading);
    const saveError       = useSelector(selectTDSSaveError);
    const addNameLoading  = useSelector(selectTDSAddNameLoading);
    const addNameError    = useSelector(selectTDSAddNameError);

    const [tdsName, setTdsName] = useState('');
    const [dca, setDca] = useState('');
    const [sdca, setSdca] = useState('');
    const [wefDate, setWefDate] = useState(todayISO());
    const [hufRate, setHufRate] = useState('');
    const [otherRate, setOtherRate] = useState('');

    const [showAddModal, setShowAddModal] = useState(false);
    const [newName, setNewName] = useState('');
    const [newCode, setNewCode] = useState('');

    useEffect(() => {
        dispatch(fetchTDSNames());
        dispatch(fetchTDSDCANames());
        dispatch(fetchTDSConfigGrid());
    }, [dispatch]);

    useEffect(() => {
        if (saveResult) {
            showToast('success', 'TDS configuration saved successfully.');
            dispatch(clearTDSSaveResult());
            setDca('');
            setSdca('');
            setWefDate(todayISO());
            setHufRate('');
            setOtherRate('');
            dispatch(clearSDCAList());
            dispatch(fetchTDSConfigGrid(tdsName));
        }
        if (saveError) {
            showToast('error', typeof saveError === 'string' ? saveError : 'Failed to save the TDS configuration.');
            dispatch(clearTDSSaveResult());
        }
    }, [saveResult, saveError, dispatch, tdsName]);

    useEffect(() => {
        if (addNameResult) {
            showToast('success', 'TDS name added successfully.');
            dispatch(clearAddNameResult());
            dispatch(fetchTDSNames());
            setShowAddModal(false);
            setNewName('');
            setNewCode('');
        }
        if (addNameError) {
            showToast('error', typeof addNameError === 'string' ? addNameError : 'Failed to add the TDS name.');
            dispatch(clearAddNameResult());
        }
    }, [addNameResult, addNameError, dispatch]);

    const handleTdsNameChange = (e) => {
        const value = e.target.value;
        setTdsName(value);
        setDca('');
        setSdca('');
        setWefDate(todayISO());
        setHufRate('');
        setOtherRate('');
        dispatch(clearSDCAList());
        dispatch(fetchTDSConfigGrid(value));
    };

    const handleDcaChange = (e) => {
        const value = e.target.value;
        setDca(value);
        setSdca('');
        dispatch(clearSDCAList());
        if (value) dispatch(fetchTDSSDCANames(value));
    };

    const handleSubmit = () => {
        if (!tdsName) { showToast('error', 'Please select a TDS Name.'); return; }
        if (!dca)      { showToast('error', 'Please select a DCA.'); return; }
        if (!sdca)     { showToast('error', 'Please select a SubDCA.'); return; }
        if (!wefDate)  { showToast('error', 'Please select a WEF Date.'); return; }
        const huf = parseFloat(hufRate);
        const other = parseFloat(otherRate);
        if (hufRate === '' || isNaN(huf) || huf <= 0) { showToast('error', 'Please enter a valid HUF Rate.'); return; }
        if (otherRate === '' || isNaN(other) || other <= 0) { showToast('error', 'Please enter a valid Other Rate.'); return; }

        dispatch(submitTDSConfigDetails({
            TdsNames:  tdsName,
            DCACode:   dca,
            SDCACode:  sdca,
            TDSDate:   wefDate,
            HUFRate:   String(huf),
            OtherRate: String(other),
            Createdby: currentUser,
        }));
    };

    const handleAddName = () => {
        if (!newName.trim()) { showToast('error', 'Please enter a TDS Name.'); return; }
        if (!newCode.trim()) { showToast('error', 'Please enter a TDS Code.'); return; }
        dispatch(submitNewTDSName({
            AddTdsName: newName.trim().toUpperCase(),
            AddTdscode: newCode.trim().toUpperCase(),
        }));
    };

    const canSubmit = tdsName && dca && sdca && wefDate && hufRate !== '' && otherRate !== '';

    const selectedTdsNameLabel = useMemo(
        () => tdsNameList.find(t => t.TDSId === tdsName)?.TDSname || '',
        [tdsNameList, tdsName]
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* Page Header */}
            <div className="max-w-5xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-6 text-white">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20 flex-shrink-0">
                            <Landmark className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">
                                Configuration
                            </span>
                            <h1 className="text-xl md:text-2xl font-black tracking-tight mt-1">TDS Nature of Payment Configuration</h1>
                            <p className="text-indigo-200 text-sm mt-0.5">
                                Map each TDS nature of payment to a DCA/SubDCA and set its HUF &amp; Other party rates, effective from a given date.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto space-y-4">

                {/* Selection form */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* TDS Name */}
                        <div className="md:col-span-2">
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    TDS Name
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(true)}
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                                >
                                    <Plus className="h-3 w-3" /> Add TDS Name
                                </button>
                            </div>
                            <select
                                value={tdsName}
                                onChange={handleTdsNameChange}
                                disabled={tdsNamesLoading}
                                className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60"
                            >
                                <option value="">-Please Select-</option>
                                {tdsNameList.map((t) => (
                                    <option key={t.TDSId} value={t.TDSId}>{t.TDSname}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {tdsName && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                            {/* DCA */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    DCA
                                </label>
                                <select
                                    value={dca}
                                    onChange={handleDcaChange}
                                    disabled={dcaLoading}
                                    className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60"
                                >
                                    <option value="">-Please Select-</option>
                                    {dcaList.map((d) => (
                                        <option key={d.TDSdcaid} value={d.TDSdcaid}>{d.TDSdcaname}</option>
                                    ))}
                                </select>
                            </div>

                            {/* SubDCA */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    SubDCA
                                </label>
                                <select
                                    value={sdca}
                                    onChange={(e) => setSdca(e.target.value)}
                                    disabled={!dca || sdcaLoading}
                                    className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60"
                                >
                                    <option value="">-Please Select-</option>
                                    {sdcaList.map((s) => (
                                        <option key={s.TDSsdcaid} value={s.TDSsdcaid}>{s.TDSsdcaname}</option>
                                    ))}
                                </select>
                                {sdcaLoading && (
                                    <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                                        <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                                    </p>
                                )}
                            </div>

                            {/* WEF Date */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    WEF Date
                                </label>
                                <input
                                    type="date"
                                    value={wefDate}
                                    max={todayISO()}
                                    onChange={(e) => setWefDate(e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all"
                                />
                            </div>

                            {/* HUF Rate */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    Ind/HUF Rate (%)
                                </label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={hufRate}
                                    onChange={(e) => setHufRate(e.target.value)}
                                    placeholder="0.00"
                                    className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all"
                                />
                            </div>

                            {/* Other Rate */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    Others Rate (%)
                                </label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={otherRate}
                                    onChange={(e) => setOtherRate(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                                    placeholder="0.00"
                                    className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all"
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2 mt-4">
                        <button
                            onClick={handleSubmit}
                            disabled={!canSubmit || saveLoading}
                            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {saveLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            Submit
                        </button>
                    </div>
                </div>

                {/* Grid of configured TDS entries */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                        <ChevronRight className="h-4 w-4 text-indigo-500" />
                        <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200">
                            {tdsName ? `Configured Entries for "${selectedTdsNameLabel}"` : 'All Configured TDS Entries'}
                        </h2>
                    </div>

                    {gridLoading ? (
                        <div className="py-10 flex flex-col items-center justify-center gap-2">
                            <Loader2 className="h-5 w-5 text-indigo-500 animate-spin" />
                            <span className="text-xs text-gray-400">Loading configuration…</span>
                        </div>
                    ) : gridError ? (
                        <div className="py-8 text-center text-sm text-rose-500">{gridError}</div>
                    ) : gridList.length === 0 ? (
                        <div className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm">
                            No TDS configuration saved yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">TDS Code</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">TDS Name</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">DCA</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">SubDCA</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">WEF Date</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">HUF Rate</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Other Rate</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {gridList.map((row, idx) => (
                                        <tr key={`${row.TDSCode}-${row.Date}-${idx}`} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                            <td className="px-4 py-3 font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap">{row.TDSCode}</td>
                                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{row.TdsNames}</td>
                                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{row.DCACode}</td>
                                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{row.SDCACode}</td>
                                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">{row.Date}</td>
                                            <td className="px-4 py-3 text-right font-semibold text-indigo-700 dark:text-indigo-400">{row.HUFRate}%</td>
                                            <td className="px-4 py-3 text-right font-semibold text-indigo-700 dark:text-indigo-400">{row.OtherRate}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Add TDS Name modal */}
            {showAddModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md">
                        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Landmark className="h-4 w-4 text-indigo-500" />
                                <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Add TDS Name</h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => { setShowAddModal(false); setNewName(''); setNewCode(''); }}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    TDS Name
                                </label>
                                <input
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-xl border-2 text-sm uppercase bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    TDS Code
                                </label>
                                <input
                                    type="text"
                                    value={newCode}
                                    maxLength={4}
                                    onChange={(e) => setNewCode(e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-xl border-2 text-sm uppercase bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all"
                                />
                            </div>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => { setShowAddModal(false); setNewName(''); setNewCode(''); }}
                                disabled={addNameLoading}
                                className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleAddName}
                                disabled={addNameLoading}
                                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 transition-colors"
                            >
                                {addNameLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TDSConfigure;
